import { addDays } from 'date-fns';

import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import uuid from 'react-uuid';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useCompany from 'src/hooks/useCompany';
import useViewCalendarYear from 'src/hooks/useViewCalendarYear';
import useUpdateCalendarPeriodApi, {
  FiscalYearPeriods,
} from 'src/services/useUpdateCalendarPeriodApi';
import { getCurrentYear } from 'src/utils/formatTime';
import {
  GLPeriodCalendarYear,
  GLPeriodCalendarYearChildren,
  formatCalendarYearTable,
} from 'src/utils/transformation';

const currentYear: number = getCurrentYear();

const BudgetPeriodsTableGridToolbar = () => {
  const {
    isPeriodEditMode,
    isCalendarYear,
    hasEditGLPeriodsPermission,
    setIsPeriodEditMode,
    selectedFYPeriod,
    setSelectedFYPeriod,
    formattedFiscalYears,
    hasPeriodErrors,
    periodErrorList,
    periodWarningList,
    getGLPeriods,
    activeFiscalYears,
  } = useViewCalendarYear();

  const {
    company: { companyId },
  } = useCompany();
  const { updateNonCalendarPeriod } = useUpdateCalendarPeriodApi();

  const handleEdit = useCallback(() => {
    setIsPeriodEditMode(true);
  }, [setIsPeriodEditMode]);

  const handleCancel = useCallback(() => {
    setSelectedFYPeriod((prevState) => {
      const { fiscalYear } = prevState;
      const data = formattedFiscalYears?.find(
        (period) => period.fiscalYear === fiscalYear
      ) as GLPeriodCalendarYear;
      return Object.assign({}, data);
    });
    setIsPeriodEditMode(false);
  }, [formattedFiscalYears, setIsPeriodEditMode, setSelectedFYPeriod]);

  const handleAddPeriod = useCallback(() => {
    setSelectedFYPeriod((prevState) => {
      const { periods, yearEndDateObj } = prevState;
      const lastIndex = periods.length - 1;
      let lastPeriod = { ...periods[lastIndex] };
      let newPeriod: GLPeriodCalendarYearChildren;

      if (periods.length === 1) {
        lastPeriod.endDate = null;
        newPeriod = {
          glPeriodId: null,
          startDate: null,
          endDate: yearEndDateObj,
          hasTransactionData: false,
          isClosed: 0,
          rowId: uuid(),
          isNewPeriod: true,
          canClosePeriod: false,
        };
        return { ...prevState, periods: [lastPeriod, newPeriod] };
      } else {
        const secondLastPeriod = { ...periods[lastIndex - 1] };
        const periodStartDate = secondLastPeriod.endDate
          ? addDays(secondLastPeriod.endDate, 1)
          : null;

        newPeriod = {
          glPeriodId: null,
          startDate: periodStartDate,
          endDate: null,
          isClosed: 0,
          hasTransactionData: false,
          rowId: uuid(),
          isNewPeriod: true,
          canClosePeriod: false,
        };
        lastPeriod.startDate = null;

        const periodList = periods.slice(0, lastIndex);
        const newPeriodList = [...periodList, newPeriod, lastPeriod];
        return { ...prevState, periods: newPeriodList };
      }
    });

    handleEdit();
  }, [handleEdit, setSelectedFYPeriod]);

  const handleSave = useCallback(() => {
    const { fiscalYear, periods } = selectedFYPeriod || {};
    const data: FiscalYearPeriods[] = periods?.map((period) => ({
      periodId: period?.glPeriodId,
      startDate: moment(period?.startDate).format('YYYY-MM-DDTHH:mm:ss'),
      endDate: moment(period?.endDate).format('YYYY-MM-DDTHH:mm:ss'),
    }));

    updateNonCalendarPeriod({
      companyId: Number(companyId),
      fiscalYear,
      periods: data,
    }).then(() => {
      toast.success('Fiscal year period successfully updated');
      const newList = [...activeFiscalYears];
      const index = activeFiscalYears?.findIndex(
        (row) => row.fiscalYear === selectedFYPeriod?.fiscalYear
      );
      if (newList.length > 0) {
        newList[index].selected = true;
      }
      setSelectedFYPeriod(newList[index]);
      setIsPeriodEditMode(false);
      getGLPeriods().then((data) => {
        setSelectedFYPeriod(
          formatCalendarYearTable(data.glPeriods).find(
            (period) => selectedFYPeriod.year <= period.year
          ) as GLPeriodCalendarYear
        );
      });
    });
  }, [
    activeFiscalYears,
    companyId,
    getGLPeriods,
    selectedFYPeriod,
    setIsPeriodEditMode,
    setSelectedFYPeriod,
    updateNonCalendarPeriod,
  ]);

  const isSelectedFYPeriodsDirty = useMemo(() => {
    const originalPeriods = formattedFiscalYears
      .find((fiscalYear) => fiscalYear?.fiscalYear === selectedFYPeriod?.fiscalYear)
      ?.periods?.map((p) => ({ ...p, rowId: undefined }));
    const selectedPeriods = selectedFYPeriod?.periods?.map((p) => ({ ...p, rowId: undefined }));
    return isEqual(selectedPeriods, originalPeriods);
  }, [formattedFiscalYears, selectedFYPeriod?.periods, selectedFYPeriod?.fiscalYear]);

  const isFutureFiscalYear = useMemo(
    () => selectedFYPeriod?.year >= currentYear,
    [selectedFYPeriod?.year]
  );

  return (
    <div className="flex items-center w-full">
      {!isCalendarYear && hasEditGLPeriodsPermission && isFutureFiscalYear && (
        <>
          <Button onClick={handleAddPeriod}>Add Period</Button>
          {isPeriodEditMode ? (
            <>
              <PopUp
                popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
                anchorAlign={{ horizontal: 'right', vertical: 'top' }}
                className="max-h-[180px] "
                content={
                  <div className="m-1 max-w-[350px]">
                    {hasPeriodErrors || periodWarningList.length ? (
                      <>
                        <PopUpContentWrapper type="Error" contents={periodErrorList} />
                        <PopUpContentWrapper type="Warning" contents={periodWarningList} />
                      </>
                    ) : (
                      <PopUpContentWrapper type="Information" contents={['No data changes']} />
                    )}
                  </div>
                }
                show={isSelectedFYPeriodsDirty || hasPeriodErrors || !!periodWarningList.length}
              >
                <Button onClick={handleSave} disabled={isSelectedFYPeriodsDirty || hasPeriodErrors}>
                  Save
                </Button>
              </PopUp>

              <Button onClick={handleCancel} color="error">
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleEdit} color="info">
                Edit
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default BudgetPeriodsTableGridToolbar;
